import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { QrCode, Download, Printer, ChevronLeft, Copy, Check, ExternalLink, Eye, RefreshCw, Building2 } from "lucide-react";
import axios from "axios";

const API = '/api';

/**
 * QR Code Manager Page
 * 
 * Manage retailer QR codes: View, Download, Copy URL, Usage stats
 */
export default function KyleRiseQRManager() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedRetailerId = searchParams.get("retailer");
  
  const [retailers, setRetailers] = useState([]);
  const [selectedRetailer, setSelectedRetailer] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => { fetchRetailers(); }, []);
  
  useEffect(() => {
    if (selectedRetailerId && retailers.length > 0) {
      const retailer = retailers.find(r => r.retailer_id === selectedRetailerId);
      if (retailer) {
        setSelectedRetailer(retailer);
        fetchQRData(selectedRetailerId);
      }
    }
  }, [selectedRetailerId, retailers]);

  const fetchRetailers = async () => {
    try {
      const res = await axios.get(`${API}/kyle-rise/retailers?active=true&limit=100`);
      setRetailers(res.data.retailers || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchQRData = async (retailerId) => {
    try {
      const res = await axios.get(`${API}/kyle-rise/retailers/${retailerId}/qr`);
      setQrData(res.data);
    } catch (err) { console.error(err); }
  };

  const selectRetailer = (retailer) => {
    setSelectedRetailer(retailer);
    fetchQRData(retailer.retailer_id);
    navigate(`/admin/kyle-rise/qr-manager?retailer=${retailer.retailer_id}`);
  };

  const copyUrl = () => {
    if (qrData?.staff_portal_url) {
      navigator.clipboard.writeText(qrData.staff_portal_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const downloadQRImage = async () => {
    if (!selectedRetailer) return;
    // Generate QR code URL for download
    window.open(`${API}/kyle-rise/retailers/${selectedRetailer.retailer_id}/qr?format=png`, '_blank');
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="kyle-rise-qr-manager">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/admin/kyle-rise")} className="p-2 hover:bg-gray-100 rounded-lg"><ChevronLeft className="w-5 h-5" /></button>
          <div><h1 className="text-xl font-bold text-gray-900">QR Code Manager</h1><p className="text-sm text-gray-500">Generate and manage retailer QR codes</p></div>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Retailer List */}
          <div className="lg:col-span-1 bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Select Retailer</h3>
            {loading ? (
              <div className="flex items-center justify-center py-8"><RefreshCw className="w-6 h-6 text-gray-400 animate-spin" /></div>
            ) : retailers.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No active retailers</p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {retailers.map((retailer) => (
                  <div
                    key={retailer.retailer_id}
                    onClick={() => selectRetailer(retailer)}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedRetailer?.retailer_id === retailer.retailer_id 
                        ? "bg-blue-50 border border-blue-200" 
                        : "hover:bg-gray-50 border border-transparent"
                    }`}
                  >
                    <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-teal-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{retailer.name}</p>
                      <p className="text-xs text-gray-500">{retailer.staff_pass_count || 0} staff passes</p>
                    </div>
                    <QrCode className="w-5 h-5 text-gray-400" />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* QR Preview */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-5">
            {!selectedRetailer ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <QrCode className="w-16 h-16 mb-4" />
                <p className="text-sm">Select a retailer to view QR code</p>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{selectedRetailer.name}</h3>
                    <p className="text-sm text-gray-500">Staff Portal QR Code</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={downloadQRImage} className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
                      <Download className="w-4 h-4" /> PNG
                    </button>
                    <button onClick={() => window.print()} className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
                      <Printer className="w-4 h-4" /> Print
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* QR Preview */}
                  <div className="aspect-square bg-white rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center p-8">
                    <div className="text-center">
                      <div className="w-48 h-48 mx-auto mb-4 bg-gray-100 rounded-lg flex items-center justify-center">
                        <QrCode className="w-32 h-32 text-gray-800" />
                      </div>
                      <p className="text-xs text-gray-500">QR Code encodes:</p>
                      <p className="text-xs text-blue-600 break-all">{qrData?.staff_portal_url}</p>
                    </div>
                  </div>

                  {/* QR Details */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Staff Portal URL</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={qrData?.staff_portal_url || ""}
                          readOnly
                          className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm"
                        />
                        <button onClick={copyUrl} className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                          {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        </button>
                        <button 
                          onClick={() => window.open(qrData?.staff_portal_url, '_blank')}
                          className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Staff Pass Price</label>
                      <p className="text-lg font-bold text-green-600">{selectedRetailer.staff_pass_price_display}</p>
                    </div>

                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Statistics</label>
                      <div className="space-y-2">
                        <div 
                          onClick={() => navigate(`/admin/kyle-rise/staff-passes?retailer_id=${selectedRetailer.retailer_id}`)}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                        >
                          <span className="text-sm text-gray-600">Total Staff Passes</span>
                          <span className="font-bold text-gray-900">{selectedRetailer.staff_pass_count || 0}</span>
                        </div>
                        <div 
                          onClick={() => navigate(`/admin/kyle-rise/staff-passes?retailer_id=${selectedRetailer.retailer_id}&status=active`)}
                          className="flex items-center justify-between p-3 bg-green-50 rounded-lg cursor-pointer hover:bg-green-100"
                        >
                          <span className="text-sm text-green-700">Active Passes</span>
                          <span className="font-bold text-green-700">{selectedRetailer.active_pass_count || 0}</span>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => navigate(`/admin/kyle-rise/retailers/${selectedRetailer.retailer_id}`)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      <Eye className="w-4 h-4" /> View Retailer Profile
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
